import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { formatCurrency } from "../utils/formatters";
import "../css/cartpage.scss";

const PROMO_CODE = "KNIT10";
const PROMO_RATE = 0.1;

const numericPrice = (value) => {
  const parsed = Number(String(value ?? "").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const findProduct = (id) =>
  productData.find((product) => String(product.id) === String(id));

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems = [],
    cartSubtotal,
    removeFromCart,
    updateQuantity,
    clearCart,
  } = useContext(CartContext);
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [promoMessage, setPromoMessage] = useState("");

  const cartLines = useMemo(
    () =>
      cartItems.flatMap((item) => {
        const product = findProduct(item.id);
        if (!product) return [];

        const quantity = Math.max(1, Number(item.quantity) || 1);
        const unitPrice = numericPrice(item.price) || numericPrice(product.price);

        return [{ item, product, quantity, unitPrice }];
      }),
    [cartItems]
  );

  const calculatedSubtotal = useMemo(
    () =>
      cartLines.reduce(
        (total, line) => total + line.unitPrice * line.quantity,
        0
      ),
    [cartLines]
  );
  const subtotal = Number.isFinite(Number(cartSubtotal))
    ? Number(cartSubtotal)
    : calculatedSubtotal;
  const shippingThreshold =
    Number(
      siteConfig.shipping?.freeShippingThreshold ??
        siteConfig.shippingThreshold
    ) || 0;
  const standardShipping = numericPrice(siteConfig.shipping?.standardRate);
  const qualifiesForFreeShipping =
    shippingThreshold > 0 && subtotal >= shippingThreshold;
  const shipping = qualifiesForFreeShipping ? 0 : standardShipping;
  const amountToFreeShipping = Math.max(0, shippingThreshold - subtotal);
  const shippingProgress = shippingThreshold
    ? Math.min(100, (subtotal / shippingThreshold) * 100)
    : 100;
  const discount = appliedPromo ? subtotal * PROMO_RATE : 0;
  const total = Math.max(0, subtotal - discount + shipping);

  const updateLineQuantity = (item, nextQuantity) => {
    if (nextQuantity < 1 || typeof updateQuantity !== "function") return;
    updateQuantity(item.id, item.size || "", item.color || "", nextQuantity);
  };

  const removeLine = (item) => {
    if (typeof removeFromCart !== "function") return;
    removeFromCart(item.id, item.size || "", item.color || "");
  };

  const applyPromotion = (event) => {
    event.preventDefault();
    const normalisedCode = promoInput.trim().toUpperCase();

    if (normalisedCode === PROMO_CODE) {
      setAppliedPromo(PROMO_CODE);
      setPromoInput(PROMO_CODE);
      setPromoMessage("10% discount applied.");
      return;
    }

    setAppliedPromo("");
    setPromoMessage(
      normalisedCode
        ? `That code is not recognised. Try ${PROMO_CODE}.`
        : "Enter a promotional code first."
    );
  };

  const goToCheckout = () => {
    navigate("/checkout", {
      state: appliedPromo ? { promoCode: appliedPromo } : undefined,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (cartLines.length === 0) {
    return (
      <main className="cart-page__root" id="main-content">
        <section
          className="cart-page__empty"
          aria-labelledby="empty-bag-title"
        >
          <div className="cart-page__empty-box">
            <p className="cart-page__eyebrow">Your bag</p>
            <h1 className="cart-page__empty-title" id="empty-bag-title">
              Your bag is empty
            </h1>
            <p className="cart-page__empty-copy">
              Discover natural-fibre layers made slowly for everyday wear.
            </p>
            <Link className="cart-page__continue-btn" to="/shop">
              Explore the collection
            </Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page__root" id="main-content">
      <div className="cart-page__main">
        <section
          className="cart-page__content cart-page__left"
          aria-labelledby="shopping-bag-title"
        >
          <header className="cart-page__header">
            <div className="cart-page__heading-group">
              <p className="cart-page__eyebrow">Your selection</p>
              <h1 className="cart-page__title" id="shopping-bag-title">
                Shopping bag
              </h1>
            </div>
            <button
              className="cart-page__clear-button"
              type="button"
              onClick={clearCart}
            >
              Clear bag
            </button>
          </header>

          <div className="cart-page__items">
            {cartLines.map(({ item, product, quantity, unitPrice }) => {
              const lineKey = `${item.id}-${item.size || "default"}-${
                item.color || "default"
              }`;
              const image = product.images?.[0] || product.image;

              return (
                <article className="cart-page__item" key={lineKey}>
                  <Link
                    className="cart-page__image-link"
                    to={`/shopDetails/${product.id}`}
                    aria-label={`View ${product.name}`}
                  >
                    <img
                      className="cart-page__image"
                      src={image}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                    />
                  </Link>

                  <div className="cart-page__item-details">
                    <div className="cart-page__item-heading">
                      <div className="cart-page__item-copy">
                        <p className="cart-page__item-category">
                          {product.category}
                        </p>
                        <h2 className="cart-page__item-name">
                          <Link
                            className="cart-page__item-link"
                            to={`/shopDetails/${product.id}`}
                          >
                            {product.name}
                          </Link>
                        </h2>
                      </div>
                      <p className="cart-page__unit-price">
                        {formatCurrency(unitPrice)}
                      </p>
                    </div>

                    {(item.color || item.size) && (
                      <p className="cart-page__item-options">
                        {[item.color, item.size].filter(Boolean).join(" · ")}
                      </p>
                    )}

                    <div className="cart-page__item-footer">
                      <div
                        className="cart-page__quantity"
                        role="group"
                        aria-label={`Quantity for ${product.name}`}
                      >
                        <button
                          className="cart-page__quantity-button"
                          type="button"
                          onClick={() => updateLineQuantity(item, quantity - 1)}
                          disabled={quantity <= 1}
                          aria-label={`Decrease ${product.name} quantity`}
                        >
                          −
                        </button>
                        <span
                          className="cart-page__quantity-value"
                          aria-live="polite"
                        >
                          {quantity}
                        </span>
                        <button
                          className="cart-page__quantity-button"
                          type="button"
                          onClick={() => updateLineQuantity(item, quantity + 1)}
                          aria-label={`Increase ${product.name} quantity`}
                        >
                          +
                        </button>
                      </div>

                      <button
                        className="cart-page__remove-button"
                        type="button"
                        onClick={() => removeLine(item)}
                        aria-label={`Remove ${product.name} from bag`}
                      >
                        Remove
                      </button>

                      <p className="cart-page__line-total">
                        {formatCurrency(unitPrice * quantity)}
                      </p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <Link className="cart-page__continue-link" to="/shop">
            <span className="cart-page__continue-arrow" aria-hidden="true">
              ←
            </span>{" "}
            Continue shopping
          </Link>
        </section>

        <aside
          className="cart-page__sidebar cart-page__right"
          aria-labelledby="bag-summary-title"
        >
          <div className="order-summary__panel">
            <h2 className="order-summary__title" id="bag-summary-title">
              Order summary
            </h2>

            <div className="order-summary__shipping-progress">
              <p className="order-summary__shipping-message">
                {qualifiesForFreeShipping
                  ? "Your order qualifies for complimentary standard delivery."
                  : `Add ${formatCurrency(
                      amountToFreeShipping
                    )} for complimentary standard delivery.`}
              </p>
              <div
                className="order-summary__progress-track"
                role="progressbar"
                aria-label="Progress towards complimentary delivery"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={Math.round(shippingProgress)}
              >
                <span
                  className="order-summary__progress-value"
                  style={{ width: `${shippingProgress}%` }}
                />
              </div>
            </div>

            <form className="form-promo" onSubmit={applyPromotion} noValidate>
              <label className="form-label" htmlFor="promo-code">
                Promotional code
              </label>
              <div className="form-inline">
                <input
                  className="form-input"
                  id="promo-code"
                  name="promoCode"
                  value={promoInput}
                  onChange={(event) => {
                    setPromoInput(event.target.value);
                    setPromoMessage("");
                  }}
                  placeholder={`Try ${PROMO_CODE}`}
                  autoComplete="off"
                  aria-describedby="promo-message"
                />
                <button className="form-button" type="submit">
                  Apply
                </button>
              </div>
              <p
                className="form-message"
                id="promo-message"
                aria-live="polite"
              >
                {promoMessage || "One code may be used per order."}
              </p>
            </form>

            <dl className="order-summary__totals">
              <div className="order-summary__row">
                <dt className="order-summary__label">Subtotal</dt>
                <dd className="order-summary__value">
                  {formatCurrency(subtotal)}
                </dd>
              </div>
              {appliedPromo && (
                <div className="order-summary__row">
                  <dt className="order-summary__label">
                    {PROMO_CODE} discount
                  </dt>
                  <dd className="order-summary__value">
                    −{formatCurrency(discount)}
                  </dd>
                </div>
              )}
              <div className="order-summary__row">
                <dt className="order-summary__label">Standard delivery</dt>
                <dd className="order-summary__value">
                  {shipping === 0 ? "Complimentary" : formatCurrency(shipping)}
                </dd>
              </div>
              <div className="order-summary__row order-summary__row--total">
                <dt className="order-summary__label">Total</dt>
                <dd className="order-summary__value">
                  {formatCurrency(total)}
                </dd>
              </div>
            </dl>
            <p className="order-summary__tax-note">
              Taxes are included. Delivery is confirmed at checkout.
            </p>

            <button
              className="order-summary__checkout-button"
              type="button"
              onClick={goToCheckout}
            >
              Continue to checkout
            </button>
            <p className="order-summary__assurance">
              Secure checkout · Payment handled by our payment partner
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
